/* ==========================================================================
   CLOSET — Shop the Look (Phase 2: selection, variant resolution, cart)

   Everything here is namespaced to the `<shop-the-look>` custom element and
   the `Stl*` identifiers below. Nothing is written to the global scope beyond
   the one element definition, and no existing theme script is patched.

   Structure, deliberately, is two layers:

     1. Portable core   — checkbox selection, variant resolution, totals and
                          cart payload generation. Knows nothing about Madrid.
     2. StlCartAdapter  — the ONLY place that touches this theme's cart
                          drawer. Swapping this object out is all that a
                          Theme App Extension port should need.
   ========================================================================== */

(function () {
	'use strict';

	if (window.customElements && window.customElements.get('shop-the-look')) return;

	/* ----------------------------------------------------------------------
	   Money — a local implementation of Shopify's money_format placeholders.

	   The theme ships a global `formatMoney`, but depending on it would tie
	   this component to Madrid. The format string itself is handed in from
	   Liquid, so this stays correct for any shop currency.
	   ---------------------------------------------------------------------- */

	function stlFormatMoney(cents, format) {
		var placeholder = /\{\{\s*(\w+)\s*\}\}/;
		var formatString = format || '${{amount}}';
		var amount = Number(cents);

		if (isNaN(amount)) amount = 0;

		function withDelimiters(precision, thousands, decimal) {
			var fixed = (amount / 100.0).toFixed(precision);
			var parts = fixed.split('.');
			var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
			return whole + (parts[1] ? decimal + parts[1] : '');
		}

		var match = formatString.match(placeholder);
		var value;

		switch (match && match[1]) {
			case 'amount_no_decimals':
				value = withDelimiters(0, ',', '.');
				break;
			case 'amount_with_comma_separator':
				value = withDelimiters(2, '.', ',');
				break;
			case 'amount_with_apostrophe_separator':
				value = withDelimiters(2, "'", '.');
				break;
			case 'amount_no_decimals_with_comma_separator':
				value = withDelimiters(0, '.', ',');
				break;
			case 'amount_no_decimals_with_space_separator':
				value = withDelimiters(0, ' ', '.');
				break;
			case 'amount_with_space_separator':
				value = withDelimiters(2, ' ', ',');
				break;
			case 'amount_with_period_and_space_separator':
				value = withDelimiters(2, ' ', '.');
				break;
			default:
				value = withDelimiters(2, ',', '.');
		}

		return formatString.replace(placeholder, value);
	}

	function stlParseJSON(element, fallback) {
		if (!element) return fallback;
		try {
			return JSON.parse(element.textContent);
		} catch (error) {
			console.warn('[shop-the-look] could not parse JSON payload', error);
			return fallback;
		}
	}

	/* A price attribute that Liquid left empty (no compare-at price on the
	   product) has to come back as null, not as 0 — 0 would read as a real
	   price of nothing. */
	function stlParsePrice(value) {
		if (value === null || value === undefined) return null;

		var trimmed = String(value).trim();
		if (trimmed === '') return null;

		var cents = Number(trimmed);
		return isFinite(cents) ? cents : null;
	}

	/* Shopify CDN URLs accept a `width` query parameter, so a variant image can
	   be re-served at the thumbnail sizes the row already requests. */
	function stlImageUrl(src, width) {
		if (!src) return '';
		var url = src.indexOf('//') === 0 ? 'https:' + src : src;
		return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
	}

	/* ----------------------------------------------------------------------
	   Cart failure classification.

	   Three genuinely different things can go wrong with one atomic add, and
	   only the difference between them is actionable for a shopper:

	     network  — the request never completed, so nothing was decided
	     cart     — Shopify received it and refused it: a variant that changed
	                under the shopper, a quantity limit, an availability rule
	     unknown  — anything else: a 5xx, an HTML error page, a body that is
	                not JSON at all

	   Shopify's response is the authority for telling `cart` from `unknown`,
	   but its error payload has no guaranteed shape, so nothing below assumes
	   a schema: it reads whatever is there and tolerates everything else.
	   Server text is never rendered — it is used to classify and to attribute,
	   and the shopper is shown this component's own wording.
	   ---------------------------------------------------------------------- */

	var STL_FAILURE_CART = 'cart';
	var STL_FAILURE_UNKNOWN = 'unknown';

	/* Marker for a fetch that never completed. Wrapping the rejection at the
	   fetch itself is what keeps a throw from anywhere further down the chain
	   from being mislabelled "we could not reach the cart". */
	function StlNetworkError(cause) {
		this.cause = cause;
	}

	function stlNormalizeText(value) {
		return String(value === null || value === undefined ? '' : value)
			.toLowerCase()
			.replace(/\s+/g, ' ')
			.trim();
	}

	/* Read a response body without trusting it: text first, JSON only when the
	   response says it is JSON or the text plainly looks like it, and a parse
	   failure is simply "no payload". Always resolves — never rejects — to an
	   object or null. */
	function stlReadPayload(response) {
		return response.text().then(
			function (text) {
				var type = '';

				try {
					type = (response.headers && response.headers.get('content-type')) || '';
				} catch (error) {
					type = '';
				}

				var trimmed = String(text || '').trim();
				var looksLikeJSON =
					type.toLowerCase().indexOf('json') !== -1 ||
					trimmed.charAt(0) === '{' ||
					trimmed.charAt(0) === '[';

				if (!trimmed || !looksLikeJSON) return null;

				try {
					var parsed = JSON.parse(trimmed);
					return parsed && typeof parsed === 'object' ? parsed : null;
				} catch (error) {
					/* Malformed, truncated, or an HTML page that happens to start
					   with a brace. Nothing usable, and nothing to render. */
					return null;
				}
			},
			function () {
				/* The body could not be read at all. */
				return null;
			}
		);
	}

	/* Flatten an unknown-shaped error payload into the only two things
	   attribution can safely use: the text it contains, and any numeric ids it
	   mentions — Shopify keys line item errors by id in some shapes, so object
	   keys count as much as values. Depth- and volume-limited, because the
	   payload is not ours and may be neither small nor sane. */
	function stlCollectHints(value, hints, depth) {
		if (value === null || value === undefined) return hints;
		if (depth > 5 || hints.text.length > 200) return hints;

		if (typeof value === 'string') {
			hints.text.push(value);
			if (/^\d{6,}$/.test(value.trim())) hints.ids.push(value.trim());
			return hints;
		}

		if (typeof value === 'number') {
			if (isFinite(value) && Math.floor(value) === value && value >= 100000) {
				hints.ids.push(String(value));
			}
			return hints;
		}

		if (Array.isArray(value)) {
			value.forEach(function (entry) {
				stlCollectHints(entry, hints, depth + 1);
			});
			return hints;
		}

		if (typeof value === 'object') {
			Object.keys(value).forEach(function (key) {
				stlCollectHints(key, hints, depth + 1);
				stlCollectHints(value[key], hints, depth + 1);
			});
		}

		return hints;
	}

	function stlErrorHints(payload) {
		var hints = stlCollectHints(payload, { text: [], ids: [] }, 0);
		var ids = {};

		hints.ids.forEach(function (id) {
			ids[id] = true;
		});

		return { haystack: stlNormalizeText(hints.text.join(' ')), ids: ids };
	}

	/* ======================================================================
	   StlCartAdapter — the only theme-specific code in this file.

	   Madrid's <cart-drawer> exposes exactly the interface product-form.js
	   uses when a product is added from a PDP:

	     getSectionsToRender()  -> the bundled sections to ask the Cart API for
	     setActiveElement(el)   -> focus return target
	     renderContents(state)  -> swap those sections in, then open the drawer

	   Using that same public surface means a Shop the Look add behaves exactly
	   like a PDP add: same markup, same cart count, same drawer, same events.
	   Nothing in cart-drawer.js / cart.js / product-form.js is modified.

	   The one place a Shop the Look add legitimately differs from a PDP add is
	   the drawer's single-product confirmation mode, which cannot describe a
	   multi-line add — see showFullCart() below. That is the whole of the extra
	   Madrid knowledge held here, and it is held here only.
	   ====================================================================== */

	var StlCartAdapter = {
		routes: function () {
			return window.routes || (window.Shopify && window.Shopify.routes) || {};
		},

		root: function () {
			var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
			return root.charAt(root.length - 1) === '/' ? root : root + '/';
		},

		addUrl: function () {
			return this.routes().cart_add_url || this.root() + 'cart/add.js';
		},

		cartUrl: function () {
			return this.routes().cart_url || this.root() + 'cart';
		},

		/* Madrid renders a drawer; a <cart-notification> build of the same
		   theme exposes the identical interface, so accept either. */
		element: function () {
			return (
				document.querySelector('cart-notification') ||
				document.querySelector('cart-drawer')
			);
		},

		sectionsToRender: function () {
			var cart = this.element();
			if (!cart || typeof cart.getSectionsToRender !== 'function') return [];
			return cart.getSectionsToRender().map(function (section) {
				return section.id;
			});
		},

		beforeAdd: function (trigger) {
			var cart = this.element();
			if (cart && typeof cart.setActiveElement === 'function') {
				cart.setActiveElement(trigger);
			}
		},

		/* Madrid's drawer has two presentation modes. Both live on the same
		   #CartDrawer element and are toggled by one class: `notification-open`
		   — reachable only when the `notification` theme setting is on —
		   collapses the drawer into a single-product "added" confirmation, its
		   CSS hiding every `.cart-item` except the one carrying `.active`, plus
		   the cart heading, the footer and the subtotal.

		   renderContents() picks that one item from `parsedState.id`. That is
		   right for a PDP add and wrong for ours: a shopper who added three
		   garments in one atomic request would be shown one of them and left
		   guessing about the other two. renderContents() takes no argument that
		   turns the mode off, so for a multi-line add we turn it off after the
		   fact, using exactly the two classes Madrid's own close() removes.

		   No Madrid file changes, and no second render: renderContents() does
		   its section swap synchronously and defers open() to a setTimeout, so
		   this lands before the drawer is ever painted. What the shopper sees is
		   the theme's ordinary full cart drawer — every line, every quantity,
		   the subtotal — which is the same state the drawer would be in had they
		   opened it from the header. */
		showFullCart: function (cart) {
			if (!cart || typeof cart.querySelector !== 'function') return;

			/* Absent when the notification setting is off, and on a
			   <cart-notification> build. Either way there is nothing to undo. */
			var notification = cart.querySelector('.notification');
			if (!notification) return;

			notification.classList.remove('notification-open');

			var highlighted = notification.querySelector('.cart-item.active');
			if (highlighted) highlighted.classList.remove('active');
		},

		/* `response` is the Cart AJAX API payload, `items` the payload we sent. */
		afterAdd: function (response, items) {
			var cart = this.element();
			var lastId = items.length ? items[items.length - 1].id : null;

			/* Same pub/sub the theme's own forms publish, so <cart-items> and
			   recipient-form re-read the cart. Guarded: these globals come from
			   Madrid and will not exist in a Theme App Extension. */
			try {
				if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
					publish(PUB_SUB_EVENTS.cartUpdate, {
						source: 'shop-the-look',
						productVariantId: String(lastId)
					});
				}
			} catch (error) {
				/* no pub/sub available — the section swap below still happens */
			}

			/* Document-level events product-form.js dispatches for third-party
			   apps. One per line added, matching a sequence of PDP adds. */
			items.forEach(function (item) {
				document.dispatchEvent(
					new CustomEvent('variant:add', {
						detail: {
							variant: { id: String(item.id) },
							quantity: item.quantity,
							formElement: null,
							sectionId: 'shop-the-look'
						}
					})
				);
			});

			if (!cart || typeof cart.renderContents !== 'function' || !response.sections) {
				window.location = this.cartUrl();
				return;
			}

			cart.classList.remove('is-empty');

			/* One line added is a PDP add in all but name, so the theme's own
			   confirmation describes it accurately and is left exactly alone.
			   Two or more is the case that confirmation cannot describe. */
			var isMultiLine = items.length > 1;

			try {
				/* renderContents highlights `.cart-item[data-product-id="<id>"]`
				   when the theme's added-to-cart notification is enabled. A
				   multi-line add has no single top-level `id`, so hand it the
				   last line we added — a real cart item — rather than letting it
				   dereference null. */
				cart.renderContents(
					Object.assign({}, response, { id: lastId })
				);
			} catch (error) {
				console.error('[shop-the-look] cart drawer render failed', error);
				if (typeof cart.open === 'function') cart.open();
			} finally {
				/* In `finally` because renderContents() sets the confirmation
				   class before the point at which it can throw: a failed render
				   must never leave the drawer open in a mode whose CSS hides
				   every line in the cart. */
				if (isMultiLine) this.showFullCart(cart);
			}

			/* Full-basket event, again mirroring product-form.js. Fire and
			   forget: the drawer is already open and correct without it. */
			var cartUrl = this.cartUrl();
			fetch(cartUrl + '.js')
				.then(function (res) {
					return res.json();
				})
				.then(function (state) {
					document.dispatchEvent(
						new CustomEvent('cart:change', {
							detail: { cart: state, sectionId: 'shop-the-look' }
						})
					);
				})
				.catch(function () {
					/* nothing depends on this */
				});
		},

		reportError: function (message, variantId) {
			try {
				if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
					publish(PUB_SUB_EVENTS.cartError, {
						source: 'shop-the-look',
						productVariantId: variantId ? String(variantId) : null,
						message: message
					});
				}
			} catch (error) {
				/* optional */
			}
		}
	};

	/* ======================================================================
	   StlItem — one product row. Portable: no theme APIs used below.
	   ====================================================================== */

	function StlItem(element, component) {
		this.el = element;
		this.component = component;

		this.include = element.querySelector('[data-stl-include]');
		this.optionInputs = Array.prototype.slice.call(
			element.querySelectorAll('[data-stl-option-input]')
		);
		this.optionGroups = Array.prototype.slice.call(
			element.querySelectorAll('[data-stl-option]')
		);
		this.variantIdField = element.querySelector('[data-stl-variant-id]');
		this.priceWrap = element.querySelector('[data-stl-price-wrap]');
		this.priceEl = element.querySelector('[data-stl-price]');
		this.compareEl = element.querySelector('[data-stl-compare-price]');
		this.imageEl = element.querySelector('img.stl-item__image');
		this.noteEl = element.querySelector('[data-stl-note]');

		this.title = element.getAttribute('data-product-title') || '';
		this.basePrice = Number(element.getAttribute('data-base-price') || 0);
		this.baseComparePrice = stlParsePrice(
			element.getAttribute('data-base-compare-at-price')
		);
		this.soldOut = element.getAttribute('data-product-unavailable') === 'true';
		this.variants = stlParseJSON(
			element.querySelector('[data-stl-variants]'),
			[]
		);

		if (this.imageEl) {
			this.imageDefault = {
				src: this.imageEl.getAttribute('src') || '',
				srcset: this.imageEl.getAttribute('srcset') || ''
			};
		}

		/* How many option positions this product actually has. Read off the
		   rendered controls so a single-value option (rendered as static text
		   with a hidden, pre-checked radio) still counts. */
		this.optionCount = this.optionInputs.reduce(function (max, input) {
			return Math.max(max, Number(input.getAttribute('data-option-position') || 0));
		}, 0);

		this.variant = null;

		/* Set only by a failed cart request that could confidently be pinned to
		   this row, and cleared the moment the shopper touches anything. It is
		   plain text in the existing note element — no live region of its own. */
		this.cartError = '';
	}

	/* Currently checked value per option position; null where unselected. */
	StlItem.prototype.selection = function () {
		var values = new Array(this.optionCount).fill(null);

		this.optionInputs.forEach(function (input) {
			var position = Number(input.getAttribute('data-option-position') || 0);
			if (!position || !input.checked) return;
			values[position - 1] = input.value;
		});

		return values;
	};

	StlItem.prototype.unselectedOptionNames = function () {
		var names = [];
		var selection = this.selection();

		this.optionGroups.forEach(function (group) {
			var position = Number(group.getAttribute('data-option-position') || 0);
			if (!position) return;
			if (selection[position - 1] === null) {
				names.push(group.getAttribute('data-option-name') || '');
			}
		});

		return names.filter(Boolean);
	};

	/* A variant matches when every option position equals the selection. */
	StlItem.prototype.matchVariant = function (selection) {
		var normalize = function (value) {
			return value === null || value === undefined
				? null
				: String(value).trim().toLowerCase();
		};

		return (
			this.variants.filter(function (variant) {
				var options = variant.options || [];
				return selection.every(function (value, index) {
					return normalize(options[index]) === normalize(value);
				});
			})[0] || null
		);
	};

	StlItem.prototype.resolveVariant = function () {
		/* A "Default Title" product has no controls at all — the single variant
		   is the answer. Same for a fully sold-out product, whose picker is not
		   rendered: it resolves, but resolves to something unavailable. */
		if (!this.optionInputs.length) {
			return this.variants[0] || null;
		}

		var selection = this.selection();
		var complete = selection.every(function (value) {
			return value !== null;
		});

		if (!complete) return null;

		return this.matchVariant(selection);
	};

	/* Availability of one option value, given every OTHER position that is
	   already chosen. This is what makes "black is sold out in M" show up the
	   moment M is picked, rather than only after the combination is submitted. */
	StlItem.prototype.valueAvailable = function (position, value) {
		var selection = this.selection();
		var normalize = function (candidate) {
			return candidate === null || candidate === undefined
				? null
				: String(candidate).trim().toLowerCase();
		};

		return this.variants.some(function (variant) {
			if (!variant.available) return false;

			var options = variant.options || [];
			if (normalize(options[position - 1]) !== normalize(value)) return false;

			return selection.every(function (selected, index) {
				if (index === position - 1) return true;
				if (selected === null) return true;
				return normalize(options[index]) === normalize(selected);
			});
		});
	};

	StlItem.prototype.isIncluded = function () {
		return !!(this.include && this.include.checked && !this.include.disabled);
	};

	StlItem.prototype.isReady = function () {
		return !!(this.variant && this.variant.available === true);
	};

	/* Total contribution: the resolved variant's price once a variant is
	   resolved, otherwise the server-rendered representative price, so the
	   summary is meaningful before the shopper has picked a size. */
	StlItem.prototype.currentPrice = function () {
		if (this.variant && typeof this.variant.price === 'number') {
			return this.variant.price;
		}
		return this.basePrice;
	};

	/* Compare-at counterpart of currentPrice(). Once a variant is resolved that
	   variant is the whole truth — including the absence of a compare-at price,
	   which is why a resolved variant never falls back to the server value. */
	StlItem.prototype.currentComparePrice = function () {
		if (this.variant) {
			return typeof this.variant.compare_at_price === 'number'
				? this.variant.compare_at_price
				: null;
		}
		return this.baseComparePrice;
	};

	StlItem.prototype.refresh = function () {
		this.variant = this.resolveVariant();

		this.refreshOptionAvailability();
		this.refreshSelectedLabels();
		this.refreshPrice();
		this.refreshImage();
		this.refreshNote();

		if (this.variantIdField) {
			this.variantIdField.value = this.variant ? this.variant.id : '';
		}

		this.el.setAttribute('data-stl-ready', this.isReady() ? 'true' : 'false');
		this.el.setAttribute('data-stl-included', this.isIncluded() ? 'true' : 'false');
	};

	StlItem.prototype.refreshOptionAvailability = function () {
		var item = this;

		this.optionInputs.forEach(function (input) {
			var position = Number(input.getAttribute('data-option-position') || 0);
			if (!position) return;

			var available = item.valueAvailable(position, input.value);
			var label = input.nextElementSibling;

			input.toggleAttribute('data-unavailable', !available);

			/* Values stay selectable on purpose: the shopper may need to move
			   colour before size. They are struck through instead, and an
			   unavailable resolved combination blocks the add. */
			if (label && label.classList.contains('stl-swatch')) {
				label.classList.toggle('stl-swatch--unavailable', !available);
				var note = label.querySelector('[data-stl-swatch-status]');
				if (note) {
					note.textContent = available ? '' : item.component.strings.soldOut;
				}
			}
		});
	};

	StlItem.prototype.refreshSelectedLabels = function () {
		var selection = this.selection();

		this.optionGroups.forEach(function (group) {
			var position = Number(group.getAttribute('data-option-position') || 0);
			var target = group.querySelector('[data-stl-option-selected]');
			if (!position || !target) return;
			if (group.classList.contains('stl-option--static')) return;
			target.textContent = selection[position - 1] || '';
		});
	};

	StlItem.prototype.refreshPrice = function () {
		if (!this.priceEl) return;

		var price = this.currentPrice();
		var compare = this.currentComparePrice();
		var onSale = typeof compare === 'number' && compare > price;

		this.priceEl.innerHTML = this.component.money(price);

		if (this.compareEl) {
			this.compareEl.innerHTML = onSale ? this.component.money(compare) : '';
			this.compareEl.hidden = !onSale;
		}

		if (this.priceWrap) {
			this.priceWrap.classList.toggle('stl-item__price--on-sale', onSale);
		}
	};

	StlItem.prototype.refreshImage = function () {
		if (!this.imageEl || !this.imageDefault) return;

		var image = this.variant && this.variant.featured_image;
		var src = image && image.src;

		if (!src) {
			this.imageEl.setAttribute('src', this.imageDefault.src);
			if (this.imageDefault.srcset) {
				this.imageEl.setAttribute('srcset', this.imageDefault.srcset);
			} else {
				this.imageEl.removeAttribute('srcset');
			}
			return;
		}

		this.imageEl.setAttribute('src', stlImageUrl(src, 480));
		this.imageEl.setAttribute(
			'srcset',
			[120, 240, 360, 480]
				.map(function (width) {
					return stlImageUrl(src, width) + ' ' + width + 'w';
				})
				.join(', ')
		);
		if (image.alt) this.imageEl.setAttribute('alt', image.alt);
	};

	/* Restrained by design: a muted hint while a choice is still outstanding,
	   the attention treatment only once something is genuinely unavailable. */
	StlItem.prototype.refreshNote = function () {
		if (!this.noteEl) return;

		var strings = this.component.strings;
		var message = '';
		var attention = false;

		if (this.cartError && this.isIncluded()) {
			/* A rejected add outranks every other note: it is the newest thing
			   the shopper needs to know about this row, and it survives until
			   they change something. */
			message = this.cartError;
			attention = true;
		} else if (this.soldOut) {
			/* The row already renders a sold-out badge; do not say it twice. */
			message = '';
		} else if (!this.isIncluded()) {
			message = '';
		} else if (!this.variant) {
			var pending = this.unselectedOptionNames();
			if (pending.length) {
				message = strings.selectOption.replace('[option]', pending.join(' + '));
			} else {
				message = strings.unavailable;
				attention = true;
			}
		} else if (!this.variant.available) {
			message = strings.soldOut;
			attention = true;
		}

		this.noteEl.textContent = message;
		this.noteEl.hidden = !message;
		this.noteEl.classList.toggle('stl-item__note--attention', attention);
	};

	StlItem.prototype.payload = function () {
		if (!this.isIncluded() || !this.isReady()) return null;
		return { id: this.variant.id, quantity: 1 };
	};

	StlItem.prototype.setCartError = function (message) {
		this.cartError = message || '';
		this.refreshNote();
	};

	/* The variant's own name ("Black / M"). A single-variant product's
	   "Default Title" is Shopify plumbing, not something to say to a shopper. */
	StlItem.prototype.variantTitle = function () {
		var title = this.variant && this.variant.title;
		if (!title) return '';
		if (String(title).trim().toLowerCase() === 'default title') return '';
		return String(title);
	};

	/* How this row should be named in a message: the product, plus the chosen
	   variant when the variant is a real choice. */
	StlItem.prototype.label = function () {
		var variantTitle = this.variantTitle();
		if (!variantTitle) return this.title;

		return this.component.strings.itemWithVariant
			.replace('[product]', this.title)
			.replace('[variant]', variantTitle);
	};

	/* --- Attribution tests. Each answers only "does the payload name me?" ---
	   None of them guesses: the caller requires a unique match before any of
	   this reaches the shopper. */

	StlItem.prototype.matchesErrorId = function (ids) {
		var variantId = this.variant && this.variant.id;
		if (variantId && ids[String(variantId)]) return true;

		var productId = this.el.getAttribute('data-product-id');
		return !!(productId && ids[String(productId)]);
	};

	/* Very short titles are ignored on purpose: a two-character title matches
	   half of any sentence by accident, and naming the wrong garment is worse
	   than naming none. */
	StlItem.prototype.matchesErrorTitle = function (haystack) {
		var title = stlNormalizeText(this.title);
		if (!haystack || title.length < 4) return false;
		return haystack.indexOf(title) !== -1;
	};

	StlItem.prototype.matchesErrorVariant = function (haystack) {
		var variantTitle = stlNormalizeText(this.variantTitle());
		if (!haystack || variantTitle.length < 3) return false;
		return haystack.indexOf(variantTitle) !== -1;
	};

	/* ======================================================================
	   <shop-the-look> — one instance per section, so several can coexist.
	   ====================================================================== */

	class ShopTheLook extends HTMLElement {}

	ShopTheLook.prototype.connectedCallback = function () {
		/* Re-entrant on purpose: the theme editor can move a section in the
		   DOM, which disconnects and reconnects the element. Binding once
		   keeps the listeners from stacking. */
		if (this.stlInitialized) return;
		this.stlInitialized = true;

		var config = stlParseJSON(this.querySelector('[data-stl-config]'), {});

		this.moneyFormat = config.moneyFormat || '${{amount}}';
		this.strings = Object.assign(
			{
				item: 'item',
				items: 'items',
				selectOption: 'Select [option]',
				soldOut: 'Sold out',
				unavailable: 'Unavailable',
				selectAtLeastOne: 'Select at least one item.',
				selectOptions: 'Select a size for each selected item.',
				someUnavailable: 'One or more selected items are unavailable.',
				adding: 'Adding',
				genericError: 'Something went wrong. Please try again.',
				networkError:
					'We could not reach the cart. Check your connection and try again.',
				cartRejected:
					'Selected items could not be added. One or more selections may have changed or become unavailable. Please review them and try again.',
				cartRejectedItem:
					'[item] could not be added. It may have become unavailable. Please review the selection and try again.',
				itemWithVariant: '[product] in [variant]',
				rowCartError: 'Could not be added'
			},
			config.strings || {}
		);

		this.countEl = this.querySelector('[data-stl-count]');
		this.totalEl = this.querySelector('[data-stl-total]');
		this.errorEl = this.querySelector('[data-stl-error]');
		this.reasonEl = this.querySelector('[data-stl-reason]');
		this.liveEl = this.querySelector('[data-stl-live]');
		this.button = this.querySelector('[data-stl-add]');
		this.buttonLabel = this.button ? this.button.textContent.trim() : '';
		this.submitting = false;

		/* Readiness is the single source of truth for whether a cart request may
		   happen. `aria-disabled` mirrors it; it does not define it. */
		this.ready = false;

		/* The visible subtotal label is read from the DOM rather than duplicated
		   in the config, so the announcement and the rendered label can never
		   drift apart. */
		var labelEl = this.querySelector('[data-stl-subtotal-label]');
		this.subtotalLabel = labelEl ? labelEl.textContent.trim() : '';

		/* Announcement bookkeeping: `announced` stays false through the first
		   refresh so the component does not speak on page load, and
		   `lastAnnouncement` suppresses repeats when a change leaves the summary
		   and the readiness reason identical. */
		this.announced = false;
		this.lastAnnouncement = '';

		this.items = Array.prototype.slice
			.call(this.querySelectorAll('[data-stl-item]'))
			.map(function (element) {
				return new StlItem(element, this);
			}, this);

		this.onChange = this.onChange.bind(this);
		this.onSubmit = this.onSubmit.bind(this);

		/* Delegated: one listener covers every checkbox and every option radio
		   in this section, and nothing else in the page. */
		this.addEventListener('change', this.onChange);
		if (this.button) this.button.addEventListener('click', this.onSubmit);

		this.refresh();
	};

	ShopTheLook.prototype.money = function (cents) {
		return stlFormatMoney(cents, this.moneyFormat);
	};

	ShopTheLook.prototype.onChange = function (event) {
		var target = event.target;
		if (!target || !target.matches) return;
		if (!target.matches('[data-stl-include], [data-stl-option-input]')) return;

		this.clearError();
		this.refresh();
	};

	/* Why the call to action cannot submit, in the order a shopper meets the
	   problems: nothing chosen, then a choice still outstanding, then something
	   genuinely unavailable. Empty string means "ready". */
	ShopTheLook.prototype.reasonFor = function (count, pending, unavailable) {
		if (count === 0) return this.strings.selectAtLeastOne;
		if (pending) return this.strings.selectOptions;
		if (unavailable) return this.strings.someUnavailable;
		return '';
	};

	ShopTheLook.prototype.refresh = function () {
		var count = 0;
		var total = 0;
		var pending = false;
		var unavailable = false;

		this.items.forEach(function (item) {
			item.refresh();
			if (!item.isIncluded()) return;
			count += 1;
			total += item.currentPrice();
			if (item.isReady()) return;

			/* An outstanding option is a prompt; anything else — an option set
			   that matches no variant, or a resolved variant that is sold out —
			   is an availability problem. */
			if (item.unselectedOptionNames().length) {
				pending = true;
			} else {
				unavailable = true;
			}
		});

		var blocked = pending || unavailable;
		this.ready = count > 0 && !blocked;

		if (this.countEl) {
			this.countEl.textContent =
				count + ' ' + (count === 1 ? this.strings.item : this.strings.items);
		}

		if (this.totalEl) {
			this.totalEl.innerHTML = this.money(total);
		}

		var reason = this.reasonFor(count, pending, unavailable);

		if (this.reasonEl) {
			this.reasonEl.textContent = reason;
			this.reasonEl.hidden = !reason;
		}

		if (this.button) {
			/* `aria-disabled`, never `disabled`: the button keeps its place in the
			   tab order and its accessible description, and onSubmit does the
			   blocking. Busy while a request is in flight. */
			this.button.setAttribute(
				'aria-disabled',
				this.submitting || !this.ready ? 'true' : 'false'
			);
			this.button.setAttribute('aria-busy', this.submitting ? 'true' : 'false');
		}

		this.announce(reason);

		return {
			count: count,
			total: total,
			blocked: blocked,
			ready: this.ready,
			reason: reason
		};
	};

	/* One announcement for the whole component: how many items, what they come
	   to, and what is still outstanding. Read out of the DOM so the money string
	   is the same text the shopper sees, with any markup in the shop's money
	   format already resolved away. */
	ShopTheLook.prototype.announce = function (reason) {
		if (!this.liveEl) return;

		var parts = [];

		if (this.countEl) parts.push(this.countEl.textContent.trim());
		if (this.totalEl) {
			parts.push(
				(this.subtotalLabel ? this.subtotalLabel + ' ' : '') +
					this.totalEl.textContent.trim()
			);
		}
		if (reason) parts.push(reason);

		var message = parts.filter(Boolean).join('. ');

		/* Silent on the first pass — arriving at a page should not trigger a
		   status announcement — and silent again whenever nothing said has
		   actually changed. */
		if (this.announced && message !== this.lastAnnouncement) {
			this.liveEl.textContent = message;
		}

		this.lastAnnouncement = message;
		this.announced = true;
	};

	/* Every row that is going into this request, paired with the line it
	   contributes, so a failure can be traced back from a cart line to the row
	   the shopper is looking at. */
	ShopTheLook.prototype.submission = function () {
		return this.items
			.map(function (item) {
				var line = item.payload();
				return line ? { item: item, line: line } : null;
			})
			.filter(Boolean);
	};

	ShopTheLook.prototype.payload = function () {
		return this.submission().map(function (entry) {
			return entry.line;
		});
	};

	/* textContent, never innerHTML: none of these strings — ours or anything
	   derived from a server response — is markup. */
	ShopTheLook.prototype.showError = function (message) {
		if (!this.errorEl) return;
		this.errorEl.textContent = message;
		this.errorEl.hidden = false;
	};

	ShopTheLook.prototype.clearError = function () {
		this.items.forEach(function (item) {
			item.cartError = '';
		});

		if (!this.errorEl) return;
		this.errorEl.textContent = '';
		this.errorEl.hidden = true;
	};

	/* One section-level message per failure, plus the theme's own cart-error
	   event. The variant id is passed only when it was actually established —
	   a subscriber that filters on it must not be handed a guess. */
	ShopTheLook.prototype.failWith = function (message, variantId) {
		this.showError(message);
		StlCartAdapter.reportError(message, variantId || null);
	};

	/* Shopify's answer decides which of the three failures this is. */
	ShopTheLook.prototype.classifyResponse = function (response, payload) {
		/* No parseable payload, or the server itself fell over: nothing here is
		   explained by the shopper's selection. */
		if (!payload) return STL_FAILURE_UNKNOWN;
		if (response.status >= 500) return STL_FAILURE_UNKNOWN;
		if (response.status >= 400) return STL_FAILURE_CART;

		/* A 200 carrying `status` is the Cart API reporting its own refusal. */
		return payload.status ? STL_FAILURE_CART : STL_FAILURE_UNKNOWN;
	};

	/* Best-effort and deliberately conservative: name a row only when the
	   payload identifies exactly one of the rows that were submitted. Anything
	   ambiguous falls through to the section-level message, because blaming the
	   wrong garment is worse than blaming none. */
	ShopTheLook.prototype.attributeFailure = function (entries, payload) {
		if (!payload || !entries.length) return null;

		var hints = stlErrorHints(payload);

		/* An id in the payload is the only unambiguous identifier there is. */
		var byId = entries.filter(function (entry) {
			return entry.item.matchesErrorId(hints.ids);
		});
		if (byId.length === 1) return byId[0].item;

		var byTitle = entries.filter(function (entry) {
			return entry.item.matchesErrorTitle(hints.haystack);
		});
		if (byTitle.length === 1) return byTitle[0].item;

		/* Two rows are named — one title contains the other, or the look holds
		   two products of the same name. The chosen variant can still separate
		   them; if it cannot, nobody is named. */
		if (byTitle.length > 1) {
			var byVariant = byTitle.filter(function (entry) {
				return entry.item.matchesErrorVariant(hints.haystack);
			});
			if (byVariant.length === 1) return byVariant[0].item;
		}

		return null;
	};

	ShopTheLook.prototype.handleResponseFailure = function (response, payload, entries) {
		/* Logged, never rendered: the server's own words can be markup, can be
		   an internal message, and are not written for a shopper. */
		console.warn(
			'[shop-the-look] cart add rejected',
			response.status,
			payload || '(no usable payload)'
		);

		if (this.classifyResponse(response, payload) === STL_FAILURE_UNKNOWN) {
			this.failWith(this.strings.genericError, null);
			return;
		}

		var item = this.attributeFailure(entries, payload);

		if (!item) {
			this.failWith(this.strings.cartRejected, null);
			return;
		}

		/* The row gets a quiet marker so the message and the garment it is
		   about are visibly connected. Still no role on that element — the
		   section error is the one thing that announces. */
		item.setCartError(this.strings.rowCartError);

		this.failWith(
			this.strings.cartRejectedItem.replace('[item]', item.label()),
			item.variant && item.variant.id
		);
	};

	ShopTheLook.prototype.setSubmitting = function (state) {
		this.submitting = state;

		if (this.button) {
			this.button.classList.toggle('stl__cta--loading', state);
			this.button.textContent = state ? this.strings.adding : this.buttonLabel;
		}

		this.refresh();
	};

	ShopTheLook.prototype.onSubmit = function (event) {
		event.preventDefault();

		/* The button is focusable and clickable in every state now, so the
		   browser no longer refuses the click for us. These three guards are the
		   whole of what keeps an invalid selection out of the cart:

		     1. a request already in flight            — no duplicate submits
		     2. readiness, recomputed on every change  — the authority
		     3. the rendered aria-disabled state       — belt and braces

		   Nothing below this point can run while any of them holds. */
		if (this.submitting) return;
		if (!this.ready) return;
		if (this.button && this.button.getAttribute('aria-disabled') === 'true') return;

		/* Rows and lines together: the lines are the request, and the rows are
		   what a failure has to be explained against. Order is DOM order, and
		   the request body is exactly what it was before. */
		var entries = this.submission();
		var items = entries.map(function (entry) {
			return entry.line;
		});

		if (!items.length) {
			this.refresh();
			return;
		}

		this.clearError();
		this.setSubmitting(true);
		StlCartAdapter.beforeAdd(this.button);

		var component = this;
		var sections = StlCartAdapter.sectionsToRender();
		var body = { items: items };

		/* Bundled section rendering, exactly as the theme's own product form
		   asks for it. Omitted when the theme exposes no cart element, in which
		   case the adapter falls back to the cart page. */
		if (sections.length) {
			body.sections = sections.join(',');
			body.sections_url = window.location.pathname;
		}

		fetch(StlCartAdapter.addUrl(), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'X-Requested-With': 'XMLHttpRequest'
			},
			body: JSON.stringify(body)
		})
			.then(null, function (error) {
				/* A rejected fetch is the ONLY thing that means "we could not
				   reach the cart". Marking it here, at the request itself, is
				   what keeps a throw from anywhere below out of that branch. */
				throw new StlNetworkError(error);
			})
			.then(function (response) {
				return stlReadPayload(response).then(function (payload) {
					return { response: response, payload: payload };
				});
			})
			.then(function (result) {
				var response = result.response;

				/* An unreadable body on an otherwise successful response stays
				   what it always was: an empty object the adapter falls back
				   from, not a failure. */
				var body = result.payload || {};

				/* The Cart API reports its own failures in the body (`status`
				   is only present on errors), including a variant that sold out
				   between page load and this click. */
				if (response.ok && !body.status) {
					StlCartAdapter.afterAdd(body, items);
					return;
				}

				/* Nothing is retried, nothing is re-submitted line by line, and
				   not one checkbox or option is touched: the shopper's selection
				   is exactly as they left it, and the next attempt is theirs to
				   make. */
				component.handleResponseFailure(response, result.payload, entries);
			})
			.catch(function (error) {
				if (error instanceof StlNetworkError) {
					console.warn(
						'[shop-the-look] cart request did not complete',
						error.cause
					);
					component.showError(component.strings.networkError);
					return;
				}

				console.error('[shop-the-look] add to cart failed', error);
				component.showError(component.strings.genericError);
			})
			.finally(function () {
				/* Always restored — an error must never strand the button. The
				   refresh inside recomputes readiness from the unchanged
				   selection, so a still-valid selection is immediately
				   submittable again. */
				component.setSubmitting(false);
			});
	};

	customElements.define('shop-the-look', ShopTheLook);
})();
